import { useEffect } from 'react';
import { Control, FieldValues, UseFormReset, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Filter, FilterGroup, RegisterType } from 'types';

import { Button } from 'components/Button';
import { CheckboxWithControl } from 'components/CheckboxWithControl';
import { Input } from 'components/Input';
import { SelectWithControl } from 'components/SelectWithControl';

import * as S from './Filters.styles';

const useFilters = (
  filters: FilterGroup[],
  register: RegisterType,
  onSubmit: () => void,
  setValue: UseFormSetValue<FieldValues>,
  defaultParams?: URLSearchParams,
  reset?: UseFormReset<FieldValues>,
  isLoading?: boolean,
  control?: Control
) => {
  const { t } = useTranslation();

  const filterValues = filters
    ?.map((item: FilterGroup) => item.filters.map((filter: Filter) => filter.name))
    .flat()
    .map((item: string) => ({
      [item]: '',
    }))
    .reduce((result, currentObject) => {
      Object.keys(currentObject).forEach((item) => {
        if (Object.prototype.hasOwnProperty.call(currentObject, item)) {
          result[item] = currentObject[item];
        }
      });

      return result;
    });

  const queryString = window.location.search;

  const handleClearFilter = () => {
    // @ts-ignore
    reset(filterValues);
    if (queryString) {
      onSubmit();
    }
  };

  const tStr = (key: Parameters<typeof t>[0]): string => String(t(key));

  const filtersIntoComponents = filters?.map((item: FilterGroup) => (
    <S.FilterContainer
      onKeyDown={(event) => event.key === 'Enter' && onSubmit()}
      key={item.id}
    >
      {item.filters.map((filter: Filter) => {
        if (filter.type === 'select' && filter.options && control) {
          return (
            <S.Filter key={filter.name} flexBasis={filter.flexBasis} flex={filter.flex}>
              <SelectWithControl
                control={control}
                name={filter.name}
                scale="xs"
                label={filter.label ? tStr(filter.label) : ''}
                options={filter.options}
                disabled={filter.disabled}
                motive="filter"
              />
            </S.Filter>
          );
        }

        if (filter.type === 'checkbox' && control) {
          return (
            <S.Filter key={filter.name} flexBasis={filter.flexBasis} flex={filter.flex}>
              <CheckboxWithControl
                disabled={filter.disabled}
                name={filter.name}
                label={filter.label ? tStr(filter.label) : ''}
                control={control}
                checkboxValue={filter.checkboxValue}
              />
            </S.Filter>
          );
        }

        return (
          <S.Filter key={filter.name} flexBasis={filter.flexBasis} flex={filter.flex}>
            <Input
              name={filter.name}
              label={filter.label ? tStr(filter.label) : ''}
              register={register}
              disabled={filter.disabled}
              // @ts-ignore
              placeholder={filter.placeholder ? tStr(filter.placeholder) : undefined}
              motive="filter"
              isCustomerCode={filter.isCustomerCode}
              hasClearIcon={filter.hasClearIcon}
              onClear={onSubmit}
              setValue={setValue}
            />
          </S.Filter>
        );
      })}

      {item.id === filters.length ? (
        <S.Buttons>
          <Button
            testID="search-button"
            onClick={onSubmit}
            motive="actionPrimary"
            scale="base"
            disabled={isLoading}
          >
            {t('common.search')}
          </Button>

          {reset ? (
            <Button
              onClick={handleClearFilter}
              type="button"
              motive="cancelSecondary"
              disabled={isLoading}
            >
              {t('common.clearFilters')}
            </Button>
          ) : undefined}
        </S.Buttons>
      ) : null}
    </S.FilterContainer>
  ));

  useEffect(() => {
    if (defaultParams) {
      for (const entry of Array.from(defaultParams.entries())) {
        const [param, value] = entry;

        setValue(param, value);
      }
    }
  }, [defaultParams]);

  return {
    filtersIntoComponents,
  };
};

export { useFilters };
